import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './Questionnaire.css';
import './PostPurchase.css';
import config from '../config/post-purchase-config.json';

// ---------------------------------------------------------------------------
// Opfølgningsskema, sendt 21 dage efter at kundens resultater er gjort
// tilgængelige. Bevidst holdt adskilt fra Questionnaire.js: det skema er
// købsflowet og må ikke tage risiko for et feedback-skema. De to deler CSS og
// tastaturgenveje, så de opfører sig ens, men ikke kode.
//
// Alle spørgsmål kan springes over. Der er derfor ingen validering af
// "påkrævet" — kun af længde på fritekst.
//
// Alle kunder får det samme skema. Vi ved ikke hvilket tolkningsniveau de
// valgte ved bestillingen, og gætter derfor ikke: ratingen af den lægefaglige
// vurdering kan besvares med "ikke relevant" af dem der ikke fik en.
// ---------------------------------------------------------------------------

const M = config.meta;

// Invitationen sendes fra Klaviyo som et Shopify-udløst flow, og ordrenummeret
// er det eneste Klaviyo kan lægge i linket. Der er derfor ikke noget token:
// linket er gættebart, præcis som købsskemaets. Det betyder ikke at data
// lækker — skemaet viser intet om kunden — men en fremmed kan indsende
// feedback på en ordre der ikke er deres. Derfor kun ét svar pr. ordre, og
// derfor er den slags rækker værd at kigge efter hvis tallene ser mærkelige ud.
//
// Uden order_id kører siden i forhåndsvisning: skemaet kan gennemses, men
// intet sendes.
const readContext = () => {
  const q = new URLSearchParams(window.location.search);
  return { order_id: (q.get('order_id') || '').trim() };
};

// Betingelsesmotor. Fire former, alle med et `field` der slås op i svarene:
//   { field, value: "x" }        → vis når feltet er x
//   { field, value: ["x", "y"] } → vis når feltet er en af værdierne
//   { field, contains: "x" }     → vis når flervalgsfeltet indeholder x
//   { field, gte: 4 } / { lte }  → vis når talsvaret er over/under en grænse
const conditionMet = (cond, lookup) => {
  if (!cond) return true;
  const actual = lookup(cond.field);
  if (cond.contains !== undefined) {
    return Array.isArray(actual) && actual.includes(cond.contains);
  }
  if (cond.gte !== undefined) return actual != null && Number(actual) >= cond.gte;
  if (cond.lte !== undefined) return actual != null && Number(actual) <= cond.lte;
  if (Array.isArray(cond.value)) return cond.value.includes(actual);
  return actual === cond.value;
};

// Gør [tekst](url) klikbart. Kunden skal kunne komme direkte videre til
// sundhed.dk eller Vena — en URL skrevet ud i brødtekst skulle de afskrive.
const LINK = /\[([^\]]+)\]\(([^)]+)\)/g;
function Linkify({ text }) {
  const dele = [];
  let sidst = 0;
  let m;
  LINK.lastIndex = 0;
  while ((m = LINK.exec(text)) !== null) {
    if (m.index > sidst) dele.push(text.slice(sidst, m.index));
    const ekstern = /^https?:/i.test(m[2]);
    dele.push(
      <a
        key={m.index}
        href={m[2]}
        {...(ekstern ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {m[1]}
      </a>
    );
    sidst = m.index + m[0].length;
  }
  if (sidst < text.length) dele.push(text.slice(sidst));
  return <>{dele}</>;
}

function PostPurchase() {
  const [ctx] = useState(readContext);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(-1); // -1 = introskærm
  // null = i gang, 'sendt' = hele skemaet gennemført, ellers nøglen på den
  // udgang kunden røg ud ad (se meta.exits i konfigurationen).
  const [done, setDone] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const isPreview = !ctx.order_id;

  const lookup = useCallback(
    (field) => (field in answers ? answers[field] : ctx[field]),
    [answers, ctx]
  );

  // Spørgsmål hvis betingelse er opfyldt lige nu. Listen kortes af ved et
  // spørgsmål der afslutter skemaet, så resten aldrig bliver synlig.
  const visible = useMemo(() => {
    const out = [];
    for (const q of config.questions) {
      if (!conditionMet(q.conditional, lookup)) continue;
      out.push(q);
      if (q.endsSurvey) break;
    }
    return out;
  }, [lookup]);

  const current = step >= 0 ? visible[step] : null;

  // Et svar kan skjule det spørgsmål man står på (fx hvis man går tilbage og
  // ændrer en forudsætning). Rul til nærmeste gyldige trin frem for at vise
  // et tomt skærmbillede.
  useEffect(() => {
    if (step >= 0 && step >= visible.length && visible.length > 0) {
      setStep(visible.length - 1);
    }
  }, [step, visible.length]);

  const setAnswer = useCallback((id, value) => {
    setAnswers(prev => {
      const next = { ...prev, [id]: value };
      // Ryd svar hvis betingelse ikke længere holder, så et skjult spørgsmål
      // ikke sender en gammel værdi med.
      const slå = (f) => (f in next ? next[f] : ctx[f]);
      for (const q of config.questions) {
        if (q.conditional && !conditionMet(q.conditional, slå) && q.id in next) {
          delete next[q.id];
        }
      }
      return next;
    });
  }, [ctx]);

  // udgang er enten null (skemaet blev gennemført) eller nøglen på den udgang
  // kunden røg ud ad. Begge dele gemmes; kun den sidste tæller som afsluttet
  // tidligt og holdes ude af tilfredshedstallene.
  const send = useCallback(async (udgang) => {
    const skaerm = udgang || 'sendt';
    if (isPreview) {
      setDone(skaerm);
      return;
    }
    setSending(true);
    setError('');
    try {
      const resp = await fetch('https://api.forms.celto.dk/post-purchase/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: ctx.order_id, answers, afsluttet_tidligt: !!udgang }),
      });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      setDone(skaerm);
    } catch (e) {
      setError('Kunne ikke sende dine svar. Prøv igen om lidt.');
    } finally {
      setSending(false);
    }
  }, [answers, ctx.order_id, isPreview]);

  const næste = useCallback(() => {
    if (current && current.endsSurvey) { send(current.endsSurvey); return; }
    if (step + 1 >= visible.length) { send(false); return; }
    setStep(step + 1);
  }, [current, step, visible.length, send]);

  const tilbage = useCallback(() => setStep(s => Math.max(0, s - 1)), []);

  // ---- Tastaturgenveje -----------------------------------------------------
  // Samme sprog som købsskemaet: Enter og pil ned går videre, pil op tilbage,
  // og et bogstav vælger den tilsvarende svarmulighed. På rating vælger 1-5.
  const næsteRef = useRef(næste);
  const tilbageRef = useRef(tilbage);
  const sendRef = useRef(send);
  næsteRef.current = næste;
  tilbageRef.current = tilbage;
  sendRef.current = send;

  // Enter starter skemaet fra introskærmen, så man kan komme i gang uden mus.
  useEffect(() => {
    if (step !== -1 || done) return undefined;
    const start = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); setStep(0); }
    };
    window.addEventListener('keydown', start);
    return () => window.removeEventListener('keydown', start);
  }, [step, done]);

  useEffect(() => {
    if (step < 0 || done) return undefined;
    const q = visible[step];
    if (!q) return undefined;

    const handler = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      const iTekstfelt = tag === 'input' || tag === 'textarea';

      // Shift+Enter skal stadig give linjeskift i en textarea.
      if (e.key === 'Enter' && !(tag === 'textarea' && e.shiftKey)) {
        e.preventDefault();
        næsteRef.current();
        return;
      }
      if (iTekstfelt || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); næsteRef.current(); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); tilbageRef.current(); return; }

      if (q.type === 'rating') {
        const n = Number(e.key);
        if (q.scale.some(s => s.value === n)) {
          e.preventDefault();
          setAnswer(q.id, n);
          setTimeout(() => næsteRef.current(), 220);
        }
        return;
      }

      if (q.type !== 'radio' && q.type !== 'checkbox') return;
      if (e.key.length !== 1) return;
      const idx = e.key.toUpperCase().charCodeAt(0) - 65;
      const opt = (q.options || [])[idx];
      if (!opt) return;
      e.preventDefault();

      if (q.type === 'radio') {
        setAnswer(q.id, opt.value);
        setTimeout(
          () => (opt.endsSurvey ? sendRef.current(opt.endsSurvey) : næsteRef.current()),
          160
        );
        return;
      }

      const valgte = Array.isArray(answers[q.id]) ? answers[q.id] : [];
      if (valgte.includes(opt.value)) {
        setAnswer(q.id, valgte.filter(v => v !== opt.value));
        return;
      }
      if (q.exclusive && opt.value === q.exclusive) { setAnswer(q.id, [opt.value]); return; }
      const uden = q.exclusive ? valgte.filter(v => v !== q.exclusive) : valgte;
      if (q.maxSelect != null && uden.length >= q.maxSelect) return;
      setAnswer(q.id, [...uden, opt.value]);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [step, done, visible, answers, setAnswer]);

  // ---- Skærmbilleder uden spørgsmål ---------------------------------------

  if (done) {
    const udgang = done === 'sendt' ? null : (M.exits[done] || M.exits.standard);
    // Vi beder kun om en anmeldelse af dem der gennemførte skemaet og gav en
    // høj samlet vurdering. Den der røg ud tidligt, eller var utilfreds, skal
    // ikke mødes af en opfordring til at fortælle omverdenen om det.
    const visTrustpilot =
      !udgang && M.trustpilot && conditionMet(M.trustpilot, lookup);
    return (
      <div className={`tf-container ${isPreview ? 'pp-har-banner' : ''}`}>
        {isPreview && <PreviewBanner />}
        <Header />
        <div className="tf-step">
          <div className="tf-completion pp-completion">
            <h2 className="tf-completion-title">{udgang ? udgang.title : M.thanksTitle}</h2>
            {udgang
              ? udgang.body.map((t, i) => <p key={i}><Linkify text={t} /></p>)
              : <p><Linkify text={M.thanksLead} /></p>}
            {visTrustpilot && <Trustpilot />}
            {!udgang && <p className="pp-medical"><Linkify text={M.thanksMedical} /></p>}
            {isPreview && (
              <div className="pp-preview-dump">
                <p className="pp-preview-dump-title">Forhåndsvisning — intet er sendt. Svar:</p>
                <pre>{JSON.stringify(answers, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === -1) {
    return (
      <div className={`tf-container ${isPreview ? 'pp-har-banner' : ''}`}>
        {isPreview && <PreviewBanner />}
        <Header />
        <div className="tf-step">
          <div className="pp-intro">
            <h1 className="pp-intro-title">{M.introTitle}</h1>
            <p className="pp-intro-lead">{M.introLead}</p>
            <p className="pp-intro-note">{M.introNote}</p>
            <p className="pp-intro-privacy">{M.introPrivacy}</p>
            <button type="button" className="tf-btn-primary" onClick={() => setStep(0)}>
              {M.introCta}
              <span className="tf-btn-arrow" aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!current) return null;

  // ---- Spørgsmål -----------------------------------------------------------

  const label = current.labelWhen && conditionMet(current.labelWhen, lookup)
    ? current.labelWhen.label
    : current.label;

  const sidsteTrin = step + 1 >= visible.length;

  return (
    <div className={`tf-container ${isPreview ? 'pp-har-banner' : ''}`}>
      {isPreview && <PreviewBanner />}
      <Header />
      <div className="tf-progress">
        <div
          className="tf-progress-bar"
          style={{ width: Math.round((step / Math.max(visible.length, 1)) * 100) + '%' }}
        />
      </div>

      <div className="tf-step">
        <div className="tf-step-content" key={current.id}>
          <div className="tf-question-number">
            <span className="tf-question-number-step">{step + 1}</span>
            <span aria-hidden="true">→</span>
          </div>

          <h2 className="pp-label">{label}</h2>
          {current.help && <p className="tf-description">{current.help}</p>}

          <div className="tf-input-wrap">
            <Felt
              q={current}
              value={answers[current.id]}
              onChange={setAnswer}
              onAdvance={næste}
              onEnd={send}
              lookup={lookup}
            />
          </div>

          {error && <div className="tf-error" role="alert">{error}</div>}

          <div className="tf-actions">
            {current.type === 'radio' || current.type === 'rating' ? (
              <button type="button" className="tf-btn-skip" onClick={næste} disabled={sending}>
                {M.skipLabel}
              </button>
            ) : (
              <button type="button" className="tf-btn-primary" onClick={næste} disabled={sending}>
                {sidsteTrin
                  ? (sending ? M.submitLoadingLabel : M.submitLabel)
                  : M.nextLabel}
                {!sending && <span className="tf-btn-arrow" aria-hidden="true">↵</span>}
              </button>
            )}
          </div>

          {step > 0 && (
            <button type="button" className="pp-back" onClick={tilbage}>← {M.backLabel}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// Samme logo og samme placering som købsskemaet. Kunden kommer fra en mail
// 30 dage efter købet og skal kunne se hvem der spørger.
function Header() {
  return (
    <div className="pp-topbar">
      <img
        className="tf-wordmark"
        src={`${process.env.PUBLIC_URL || ''}/celto-wordmark.png`}
        alt="Celto"
      />
    </div>
  );
}

// Opfordring til en anmeldelse, kun på takkeskærmen og kun ved en høj samlet
// vurdering. Svarene er allerede sendt når denne vises, så et klik koster ikke
// feedbacken — værst kan kunden lukke fanen uden at skrive noget.
function Trustpilot() {
  const T = M.trustpilot;
  return (
    <div className="pp-trustpilot">
      <p className="pp-trustpilot-title">{T.title}</p>
      <p className="pp-trustpilot-body">{T.body}</p>
      <a
        className="tf-btn-primary pp-trustpilot-btn"
        href={T.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="pp-trustpilot-stars" aria-hidden="true">★★★★★</span>
        {T.cta}
      </a>
      {T.note && <p className="pp-note pp-trustpilot-note">{T.note}</p>}
    </div>
  );
}

function PreviewBanner() {
  return (
    <div className="pp-preview" role="status">
      <strong>Forhåndsvisning</strong> — der er intet <code>order_id</code> i linket, så intet bliver sendt.
    </div>
  );
}

// ---------------------------------------------------------------------------
// Felttyper
// ---------------------------------------------------------------------------

function Felt({ q, value, onChange, onAdvance, onEnd, lookup }) {
  switch (q.type) {
    case 'radio':
      return <Radio q={q} value={value} onChange={onChange} onAdvance={onAdvance} onEnd={onEnd} />;
    case 'checkbox':
      return <Checkbox q={q} value={value} onChange={onChange} />;
    case 'rating':
      return <Rating q={q} value={value} onChange={onChange} onAdvance={onAdvance} />;
    case 'rating-matrix':
      return <Matrix q={q} value={value} onChange={onChange} lookup={lookup} />;
    case 'text':
    case 'textarea':
      return <Fritekst q={q} value={value} onChange={onChange} />;
    default:
      return null;
  }
}

function Radio({ q, value, onChange, onAdvance, onEnd }) {
  return (
    <div className="tf-radio-group" role="radiogroup" aria-label={q.label}>
      {q.options.map((opt, i) => {
        const valgt = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={valgt}
            className={`tf-radio-card ${valgt ? 'tf-radio-card--selected' : ''}`}
            onClick={() => {
              if (valgt) { onChange(q.id, ''); return; }
              onChange(q.id, opt.value);
              // Et valg kan afslutte skemaet med sin egen besked — fx når
              // prøven slet ikke er taget endnu.
              setTimeout(() => (opt.endsSurvey ? onEnd(opt.endsSurvey) : onAdvance()), 160);
            }}
          >
            <span className="tf-radio-letter">{String.fromCharCode(65 + i)}</span>
            <span className="tf-radio-text">
              <span className="tf-radio-label">{opt.label}</span>
              {opt.note && <span className="tf-radio-desc">{opt.note}</span>}
            </span>
            {valgt && <span className="tf-radio-check" aria-hidden="true">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

function Checkbox({ q, value, onChange }) {
  const valgte = Array.isArray(value) ? value : [];
  const fuld = q.maxSelect != null && valgte.length >= q.maxSelect;

  const toggle = (kode) => {
    if (valgte.includes(kode)) { onChange(q.id, valgte.filter(v => v !== kode)); return; }
    // "Intet – jeg er tilfreds" giver ikke mening sammen med et forbedringsønske.
    if (q.exclusive && kode === q.exclusive) { onChange(q.id, [kode]); return; }
    const uden = q.exclusive ? valgte.filter(v => v !== q.exclusive) : valgte;
    if (q.maxSelect != null && uden.length >= q.maxSelect) return;
    onChange(q.id, [...uden, kode]);
  };

  return (
    <>
      <div className="tf-radio-group" role="group" aria-label={q.label}>
        {q.options.map((opt, i) => {
          const på = valgte.includes(opt.value);
          const spærret = fuld && !på && opt.value !== q.exclusive;
          return (
            <button
              key={opt.value}
              type="button"
              role="checkbox"
              aria-checked={på}
              aria-disabled={spærret}
              className={`tf-radio-card ${på ? 'tf-radio-card--selected' : ''} ${spærret ? 'pp-card--muted' : ''}`}
              onClick={() => toggle(opt.value)}
            >
              <span className="tf-radio-letter tf-radio-letter--check">
                {String.fromCharCode(65 + i)}
              </span>
              <span className="tf-radio-text">
                <span className="tf-radio-label">{opt.label}</span>
                {opt.note && <span className="tf-radio-desc">{opt.note}</span>}
              </span>
              {på && <span className="tf-radio-check" aria-hidden="true">✓</span>}
            </button>
          );
        })}
      </div>
      {q.maxSelect != null && (
        <p className="pp-counter">{valgte.length} af højst {q.maxSelect} valgt</p>
      )}
    </>
  );
}

// Skalaen står ét sted, over knapperne. Ellers skal man læse en forklaring
// under kortet og se op igen for at bruge den.
function Skala({ scale }) {
  const sidst = scale[scale.length - 1];
  return (
    <p className="pp-skala">
      <span>1 = {scale[0].label}</span>
      <span aria-hidden="true"> · </span>
      <span>{sidst.value} = {sidst.label}</span>
    </p>
  );
}

function Rating({ q, value, onChange, onAdvance }) {
  return (
    <div className="pp-rating">
      <Skala scale={q.scale} />
      <div className="pp-rating-row" role="radiogroup" aria-label={q.label}>
        {q.scale.map(s => {
          const valgt = value === s.value;
          return (
            <button
              key={s.value}
              type="button"
              role="radio"
              aria-checked={valgt}
              aria-label={`${s.value} – ${s.label}`}
              className={`pp-rating-btn ${valgt ? 'pp-rating-btn--selected' : ''}`}
              onClick={() => {
                if (valgt) { onChange(q.id, undefined); return; }
                onChange(q.id, s.value);
                setTimeout(onAdvance, 220);
              }}
            >
              <span className="pp-rating-num">{s.value}</span>
              <span className="pp-rating-text">{s.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Rating-matrix. Ét kort pr. kategori frem for en bred tabel — en tabel med
// syv rækker og seks kolonner er ulæselig på en telefon, og skemaet er
// mobiloptimeret.
function Matrix({ q, value, onChange, lookup }) {
  const svar = value && typeof value === 'object' ? value : {};
  const kategorier = q.categories.filter(k => conditionMet(k.conditional, lookup));

  const sæt = (kategoriId, v) => {
    const næste = { ...svar };
    if (næste[kategoriId] === v) delete næste[kategoriId];
    else næste[kategoriId] = v;
    onChange(q.id, næste);
  };

  return (
    <div className="pp-matrix">
      <Skala scale={q.scale} />
      {kategorier.map(k => (
        <div className="pp-matrix-card" key={k.id}>
          <p className="pp-matrix-label">{k.label}</p>
          <div className="pp-matrix-row" role="radiogroup" aria-label={k.label}>
            {q.scale.map(s => {
              const valgt = svar[k.id] === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  role="radio"
                  aria-checked={valgt}
                  aria-label={`${k.label}: ${s.label}`}
                  title={s.label}
                  className={`pp-matrix-btn ${valgt ? 'pp-matrix-btn--selected' : ''}`}
                  onClick={() => sæt(k.id, s.value)}
                >
                  {s.value}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            role="radio"
            aria-checked={svar[k.id] === q.naValue}
            className={`pp-na ${svar[k.id] === q.naValue ? 'pp-na--selected' : ''}`}
            onClick={() => sæt(k.id, q.naValue)}
          >
            {q.naLabel}
          </button>
        </div>
      ))}
    </div>
  );
}

function Fritekst({ q, value, onChange }) {
  const v = value || '';
  const tilbage = q.maxLength ? q.maxLength - v.length : null;
  const Tag = q.type === 'textarea' ? 'textarea' : 'input';
  return (
    <>
      <Tag
        className={q.type === 'textarea' ? 'tf-textarea' : 'tf-input'}
        value={v}
        maxLength={q.maxLength}
        rows={q.type === 'textarea' ? (q.rows || 4) : undefined}
        onChange={(e) => onChange(q.id, e.target.value.slice(0, q.maxLength || undefined))}
      />
      {q.maxLength != null && (
        <p className={`pp-counter ${tilbage < 40 ? 'pp-counter--lav' : ''}`}>
          {tilbage} tegn tilbage
        </p>
      )}
    </>
  );
}

export default PostPurchase;
